import { useAlert } from '@ads/components-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  getApiV1ExamRevisionBotexamidLangGridQueryKey,
  usePatchApiV1ExamRevisionLangBotexamlangidActiveActive,
} from '@/shared/api/generated';

/**
 * Ativa ou inativa um idioma de uma prova em revisão via
 * `PATCH /api/v1/exam/revision/lang/{botExamLangId}/active/{active}`, invalidando
 * a grid de idiomas ao concluir. A inativação propaga em cascata para as questões
 * e alternativas do idioma; a confirmação do operador é responsabilidade da UI.
 *
 * @param botExamId - ID da prova bot à qual o idioma pertence (para invalidação)
 * @returns Handler de ativação/inativação e estado de loading
 *
 * @example
 * ```tsx
 * const { toggleActive, isLoading } = useToggleRevisionLangActive(botExamId);
 * toggleActive(botExamLangId, false); // inativa o idioma
 * ```
 */
export function useToggleRevisionLangActive(botExamId: number) {
  const queryClient = useQueryClient();
  const { alert } = useAlert();
  const { t } = useTranslation('examRevision');

  const mutation = usePatchApiV1ExamRevisionLangBotexamlangidActiveActive({
    mutation: {
      onSuccess: (_data, variables) => {
        alert({
          alertType: 'success',
          title: variables.active
            ? t('langs.toast.activateSuccess')
            : t('langs.toast.deactivateSuccess'),
        });
        void queryClient.invalidateQueries({
          queryKey: getApiV1ExamRevisionBotexamidLangGridQueryKey(botExamId),
        });
      },
      onError: (_error, variables) =>
        alert({
          alertType: 'warning',
          title: variables.active
            ? t('langs.toast.activateError')
            : t('langs.toast.deactivateError'),
        }),
    },
  });

  const toggleActive = (
    botExamLangId: number,
    active: boolean,
    callbacks?: { onSuccess?: () => void },
  ) => {
    mutation.mutate(
      { botExamLangId, active },
      callbacks?.onSuccess ? { onSuccess: callbacks.onSuccess } : undefined,
    );
  };

  return { toggleActive, isLoading: mutation.isPending } as const;
}
